import React from 'react';
import { Button, Card, Modal, Space, Table, TablePaginationConfig } from 'antd';
import {
  UnlockOutlined,
  LockOutlined,
  ExclamationCircleOutlined,
  FormOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';

import { ApiUser } from '@services/types';
import api from '@services/api';
import styles from './api-users-table.module.scss';

import { useHistory } from 'react-router-dom';
import { Navigation } from '@constants/Navigation';

const { Column } = Table;

const AdminApiUsers = () => {
  const [apiUsers, setApiUsers] = React.useState<ApiUser[]>([]);
  const history = useHistory();
  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    total: apiUsers.length,
  });

  React.useEffect(() => {
    api.administration.getApiUsers().then(setApiUsers);
  }, []);

  const handleTableChange = React.useCallback((newPagination: TablePaginationConfig) => {
    setPagination(newPagination);
  }, []);

  const showLockConfirm = (user: ApiUser) => {
    Modal.confirm({
      title: user.isLocked ? 'Are you sure unlock this user?' : 'Are you sure lock this user?',
      icon: <ExclamationCircleOutlined />,
      okText: user.isLocked ? 'Unlock' : 'Lock',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          if (user.isLocked) {
            await api.administration.unlockApiUser(user.id);
          } else {
            await api.administration.lockApiUser(user.id);
          }
          toast.success(user.isLocked ? 'ApiUser was unlocked successfully' : 'ApiUser was locked successfully');
          api.administration.getApiUsers().then(setApiUsers);
        } catch (error) {
          console.log('ApiUser was not locked ~ error', error);
          toast.error('ApiUser was not locked');
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const apiCount = (pagination.pageSize || 0) * (pagination.current || 0);
  const apiItem = (apiCount || 0) - ((pagination.pageSize || 0) - 1);

  return (
    <Card
      title={`Api Users: ${apiItem}-${apiCount} / ${apiUsers.length}`}
      extra={
        <Button type="primary" onClick={() => history.push(Navigation.CreateApiUser)}>
          Add
        </Button>
      }
      bodyStyle={{ padding: 0 }}
    >
      <Table
        dataSource={apiUsers}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey={(record: ApiUser) => record.id}
        onRow={(record: ApiUser) => ({ className: styles[record.isLocked ? 'locked' : 'unlock'] })}
      >
        <Column title="API user key" dataIndex="apiKey" key="apiKey" />
        <Column title="Description" dataIndex="description" key="description" />
        <Column title="Role" dataIndex="roles" key="role" render={(roles: ApiUser['roles']) => roles.join(', ')} />
        <Column title="Scope" dataIndex="etc" render={(etc: ApiUser['etc']) => etc.perimeterNames?.join(', ')} />
        <Column
          title="Actions"
          key="action"
          width="100px"
          align="center"
          fixed="right"
          render={(text, user: ApiUser) => (
            <Space size="middle">
              <InfoCircleOutlined onClick={() => history.push(Navigation.ApiUserInfo.replace(':id', user.id))} />
              <FormOutlined onClick={() => history.push(Navigation.UpdateApiUser.replace(':id', user.id))} />
              {user.isLocked ? (
                <UnlockOutlined onClick={() => showLockConfirm(user)} />
              ) : (
                <LockOutlined onClick={() => showLockConfirm(user)} />
              )}
            </Space>
          )}
        />
      </Table>
    </Card>
  );
};

export default React.memo(AdminApiUsers);
